import { EllipsisOutlined } from '@ant-design/icons';
import { useContainerSize } from '@bimser/core';
import { Button } from 'antd';
import classNames from 'classnames/bind';
import React, { useEffect, useRef, useState } from 'react';
import Styles from './assets/Styles.scss';
import { ButtonList } from './ButtonList';
import { IButtonOverflowItem } from './entities/IButtonOverflowItem';
import { IButtonOverflowProps } from './entities/IButtonOverflowProps';
import { MeasureChildren } from './MeasureChildren';
import { OverflowButtons } from './OverflowButtons';
import debounce from 'lodash/debounce';
const cx = classNames.bind(Styles);

const overflowButtonWidth = 35;

const BCButtonOverflow: React.FC<IButtonOverflowProps> = props => {
	const measureContainerRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const calculatedButtonMeasures = useRef<number[]>();
	const [visibleItems, setVisibleItems] = useState<IButtonOverflowItem[]>(props.items);
	const [overflowItems, setOverflowItems] = useState<IButtonOverflowItem[]>([]);
	const [calculate, setCalculate] = useState<boolean>(true);

	const dimensions = useContainerSize(containerRef, 100);

	useEffect(() => {
		setCalculate(true);
	}, [props.items]);

	const measureButtons = () => {
		if (!measureContainerRef.current) return;

		const children = Array.from(measureContainerRef.current.children) as HTMLDivElement[];
		const measures: number[] = [];

		props.items.forEach((_, index) => {
			const buttonElement = children[index];
			if (buttonElement) {
				const buttonWidth = buttonElement.offsetWidth;
				measures.push(buttonWidth + 5); // + Button Gap
			}
		});

		calculatedButtonMeasures.current = measures;
		setCalculate(false);
	};

	const _calculateVisibleItems = () => {
		const visible: IButtonOverflowItem[] = [];
		const overflow: IButtonOverflowItem[] = [];

		let totalWidth = 0;
		measureButtons();
		const measures = calculatedButtonMeasures.current;
		if (measures) {
			props.items.forEach((button, index) => {
				if (totalWidth + measures[index] > dimensions.width - overflowButtonWidth) {
					overflow.push(button);
				} else {
					visible.push(button);
				}
				totalWidth += measures[index];
			});

			setVisibleItems(visible);
			setOverflowItems(overflow);
		}
	};

	const calculatevisibleItems = debounce(_calculateVisibleItems, 500, {
		leading: false,
		trailing: true
	});

	useEffect(() => {
		_calculateVisibleItems();
		calculatevisibleItems();

		return () => {
			calculatevisibleItems.cancel();
		};
	}, [dimensions.width, props.items]);

	return (
		<>
			<div
				ref={containerRef}
				className={cx({ container: true, alignRight: props.alignRight })}
				style={props.style}
			>
				<ButtonList
					items={visibleItems}
					dropdownProps={props.dropdownProps}
				/>
				<OverflowButtons
					items={overflowItems}
					dropdownProps={props.dropdownProps}
				>
					<Button
						icon={<EllipsisOutlined />}
						{...props.overflowButtonProps}
					/>
				</OverflowButtons>
			</div>
			<MeasureChildren
				ref={measureContainerRef}
				visible={calculate}
				items={props.items}
			/>
		</>
	);
};

export { BCButtonOverflow, ButtonList, IButtonOverflowItem };
